import { ApplicationForm } from '../types';
import { LIMITS } from '../components/ProjectContent';
import { COMMENT_MAX_LENGTH, COMMENT_MIN_LENGTH } from '../components/evaluation/EvaluationPanel';

// Helper type for form data
type FormData = Partial<ApplicationForm>;

export const checkContestInfoCompletion = (_formData: FormData): boolean => {
  // Contest info is typically just informational, so it's always complete
  // You can add specific checks here if needed
  return true;
};

export const checkBasicInfoCompletion = (formData: FormData): boolean => {
  return !!(
    formData.projectTitle &&
    formData.interdisciplinaryProject &&
    formData.transdisciplinaryProject &&
    formData.genderApproachProject &&
    formData.openScience &&
    (formData.rcer?.length ?? 0) > 0 &&
    formData.keywords &&
    formData.evaluationArea &&
    formData.oecdArea &&
    formData.oecdSubarea &&
    (formData.sdgGoals?.length ?? 0) > 0 &&
    (formData.executionRegions?.length ?? 0) > 0
  );
};

export const checkContentCompletion = (formData: FormData): boolean => {
  return !!(
    formData.executiveSummary &&
    formData.executiveSummary.length <= LIMITS.executiveSummary &&
    formData.projectDocument
  );
};

export const checkTeamInfoCompletion = (formData: FormData): boolean => {
  const hasInternationalCOI = (formData.coInvestigators || []).some(
    (ci: any) => ci.role === 'COI' && ci.institutionType === 'Internacional'
  );
  const coiJustificationOk =
    hasInternationalCOI || !!(formData.internationalCoiJustification?.trim());

  return ((!formData.coInvestigators || formData.coInvestigators.every(
    (ci: any) => {
      // RUN is only required when documentType is 'run' (or undefined for backwards compatibility)
      const runRequired = !ci.documentType || ci.documentType === 'run';
      const runComplete = runRequired ? !!ci.run : true;
      const basicFields = ci.name && ci.email && runComplete;
      if (ci.role === 'IP') {
        return basicFields && ci.doctorateYear && ci.doctorateMonth && ci.affiliation && ci.facultadUC && ci.unidadAcademicaUC;
      } else if (ci.role === 'IA') {
        return basicFields && ci.affiliation && ci.facultadUC && ci.unidadAcademicaUC;
      } else {
        return basicFields && ci.affiliation && ci.institutionType;
      }
    }
  )) && !!(formData.principalInvestigator?.name &&
      formData.principalInvestigator?.email &&
      formData.principalInvestigator?.run)
    && coiJustificationOk
    );
};

// Detailed function to get field information for ALL team members
export const getTeamInfoMissingFields = (formData: FormData): Array<{name: string, isComplete: boolean, fields: Array<{name: string, isComplete: boolean}>}> => {
  const members: Array<{name: string, isComplete: boolean, fields: Array<{name: string, isComplete: boolean}>}> = [];


  // Check Co-investigators
  if (formData.coInvestigators) {
    formData.coInvestigators.forEach((ci: any, index: number) => {
      const memberName = ci.name || `Integrante ${index + 1}`;

      // RUN/passport field is only required when documentType is 'run' (or undefined for backwards compatibility)
      const runRequired = !ci.documentType || ci.documentType === 'run';
      const runComplete = runRequired ? !!ci.run : true;

      const basicFields = [
        { name: 'nombre', isComplete: !!ci.name },
        { name: 'correo', isComplete: !!ci.email }
      ];

      // Only add RUN/pasaporte to the list if it's required
      if (runRequired) {
        basicFields.push({ name: 'RUN/pasaporte', isComplete: runComplete });
      }

      let roleFields: Array<{name: string, isComplete: boolean}> = [];
      if (ci.role === 'IP') {
        roleFields = [
          { name: 'año de doctorado', isComplete: !!ci.doctorateYear },
          { name: 'mes de doctorado', isComplete: !!ci.doctorateMonth },
          { name: 'institución', isComplete: !!ci.affiliation },
          { name: 'facultad UC', isComplete: !!ci.facultadUC },
          { name: 'unidad académica UC', isComplete: !!ci.unidadAcademicaUC }
        ];
      } else if (ci.role === 'IA') {
        roleFields = [
          { name: 'institución', isComplete: !!ci.affiliation },
          { name: 'facultad UC', isComplete: !!ci.facultadUC },
          { name: 'unidad académica UC', isComplete: !!ci.unidadAcademicaUC }
        ];
      } else if (ci.role === 'COI') {
        roleFields = [
          { name: 'institución', isComplete: !!ci.affiliation },
          { name: 'tipo de institución', isComplete: !!ci.institutionType }
        ];
      }

      const allFields = [...basicFields, ...roleFields];
      const memberIsComplete = allFields.every(field => field.isComplete);

      members.push({
        name: memberName,
        isComplete: memberIsComplete,
        fields: allFields
      });
    });
  }

  // Team-level: COI justification required when no international COI
  const hasInternationalCOI = (formData.coInvestigators || []).some(
    (ci: any) => ci.role === 'COI' && ci.institutionType === 'Internacional'
  );
  if (!hasInternationalCOI) {
    const justOk = !!(formData.internationalCoiJustification?.trim());
    members.push({
      name: 'Justificación ausencia COI extranjero',
      isComplete: justOk,
      fields: [{ name: 'justificación requerida (sin Co-Investigador de institución extranjera)', isComplete: justOk }]
    });
  }

  return members;
};

// Function to get specific missing basic info fields
export const getBasicInfoMissingFields = (formData: FormData): Array<{name: string, isComplete: boolean}> => {
  const fields = [
    { name: 'Título del proyecto', isComplete: !!formData.projectTitle },
    { name: 'Proyecto interdisciplinario', isComplete: !!formData.interdisciplinaryProject },
    { name: 'Proyecto transdisciplinario', isComplete: !!formData.transdisciplinaryProject },
    { name: 'Enfoque de género', isComplete: !!formData.genderApproachProject },
    { name: 'Ciencia abierta', isComplete: !!formData.openScience },
    { name: 'Comité de ética (RCER)', isComplete: (formData.rcer?.length ?? 0) > 0 },
    { name: 'Palabras clave', isComplete: !!formData.keywords },
    { name: 'Línea de investigación', isComplete: !!formData.evaluationArea },
    { name: 'Área OECD', isComplete: !!formData.oecdArea },
    { name: 'Subárea OECD', isComplete: !!formData.oecdSubarea },
    { name: 'Objetivos de desarrollo sostenible', isComplete: (formData.sdgGoals?.length ?? 0) > 0 },
    { name: 'Regiones de ejecución', isComplete: (formData.executionRegions?.length ?? 0) > 0 }
  ];

  return fields;
};

export const checkBudgetCompletion = (formData: FormData): boolean => {
  // Only check that the detailed budget file is uploaded
  return !!(formData.budget?.detailedFile);
};

export const checkPublicationsCompletion = (formData: FormData): boolean => {
  return !!(
    formData.ipCvData &&
    formData.ipCvData.length > 0 &&
    formData.ipCvData.every((cvData: any) =>
      cvData.cvNarrative &&
      cvData.publications &&
      cvData.publications.length >= 0 &&
      cvData.publications.length <= 5 &&
      cvData.publications.every(
        // Requerimiento 2026-07-21: toda publicación debe tener archivo adjunto
        // (única evidencia que garantiza el acceso del evaluador; el DOI/enlace
        // es solo referencia). Un DOI de pago ya no basta.
        (pub: any) => !!pub.fileId
      )
    ) &&
    formData.hasConflictOfInterest &&
    (formData.hasConflictOfInterest === 'NO' ||
     (formData.hasConflictOfInterest === 'SI' &&
      formData.conflictOfInterestList?.every(
        (conflict: any) => conflict.name && conflict.institution
      ))) &&
    // Check that IR has DOI file
    formData.principalInvestigatorDoiFile &&
    // Check that IP co-investigator has DOI file (if exists)
    (!formData.coInvestigators?.some((ci: any) => ci.role === 'IP') || formData.coInvestigatorDoiFile) &&
    // Check that all COI co-investigators have commitment letters
    (formData.coInvestigators?.filter((ci: any) => ci.role === 'COI').every((coi: any) => coi.commitmentLetter) ?? true) &&
    // Check institution commitment letters (optional field, max 5 files)
    (!formData.institutionCommitmentLetters || formData.institutionCommitmentLetters.length >= 0) &&
    ((formData.suggestedReviewers && formData.suggestedReviewers.length > 0)
        ? formData.suggestedReviewers.every(reviewer => reviewer.name && reviewer.institution)
        : true)
  );
};

export const checkReviewCompletion = (formData: FormData): boolean => {
  // Check that incompatibility declaration is complete
  const incompatibilityDeclared = !!(
    formData.noIncompatibilities ||
    formData.finalReportsUC ||
    formData.finalReportsANID ||
    formData.duplicateFunding ||
    formData.otherIncompatibilities
  );

  // Check that if "other incompatibilities" is selected, details are provided
  const otherIncompatibilityValid = !formData.otherIncompatibilities || 
    (formData.otherIncompatibilities && formData.otherIncompatibilitiesDetails?.trim());

  // Check that information declaration is confirmed
  const informationDeclarationComplete = Boolean(formData.informationDeclaration);

  return Boolean(incompatibilityDeclared && otherIncompatibilityValid && informationDeclarationComplete);
};

// Master function that checks all sections
export const checkAllSectionsCompletion = (formData: FormData): boolean => {
  return checkContestInfoCompletion(formData) &&
         checkBasicInfoCompletion(formData) &&
         checkContentCompletion(formData) &&
         checkTeamInfoCompletion(formData) &&
         checkBudgetCompletion(formData) &&
         checkPublicationsCompletion(formData) &&
         checkReviewCompletion(formData);
};

// Function to check completion for evaluation steps
export const checkEvaluationStepCompletion = (stepId: string, formData: any): boolean => {
  switch (stepId) {
    case 'instructions':
      // Instructions step is always complete once viewed
      return true;
    case 'conflict-declaration':
      // Conflict declaration step is complete when no conflict is declared
      return formData.noConflictDeclared === true;
    case 'evaluation':
      // Evaluation step is complete when all criteria have scores/comments and
      // the overall perception is written (la recomendación fue eliminada, P5).
      if (!formData.criteriaEvaluations) {
        return false;
      }

      // Define the criteria IDs that should be evaluated
      const requiredCriteria = [
        'originality_risk',
        'national_relevance',
        'global_scalability',
        'international_collaboration',
        'technical_viability',
        'results_quality'
      ];

      // Check all criteria evaluations
      const criteriaComplete = requiredCriteria.every(criteriaId => {
        const evaluation = formData.criteriaEvaluations[criteriaId];
        return evaluation &&
               evaluation.score !== null &&
               evaluation.score !== undefined &&
               evaluation.score !== '' &&
               evaluation.comments &&
               evaluation.comments.trim() !== '';
      });

      // Budget comments are optional (P3): they do not affect completion.

      const overallCommentsComplete = formData.overallComments &&
           formData.overallComments.trim() !== '' &&
           formData.overallComments.trim().length >= COMMENT_MIN_LENGTH &&
           formData.overallComments.trim().length <= COMMENT_MAX_LENGTH

      return criteriaComplete && overallCommentsComplete;
    case 'summary':
      // Summary step is complete when evaluation is submitted
      return false; // This will be handled differently since we don't track submission in form data
    default:
      return false;
  }
};

// Function to check completion for a specific step by ID
export const checkStepCompletion = (stepId: string, formData: FormData): boolean => {
  switch (stepId) {
    case 'contest-info':
      return checkContestInfoCompletion(formData);
    case 'basic':
      return checkBasicInfoCompletion(formData);
    case 'content':
      return checkContentCompletion(formData);
    case 'team':
      return checkTeamInfoCompletion(formData);
    case 'budget':
      return checkBudgetCompletion(formData);
    case 'publications':
      return checkPublicationsCompletion(formData);
    case 'review':
      return checkReviewCompletion(formData);
    default:
      return false;
  }
};